import { useMemo } from "react";

import { useSearchParams } from "@shared/react-router-dom/react-router-dom";

const INVITE_TOKEN_COOKIE = "patient_invite_token";
const INVITE_INVITER_NAME_COOKIE = "patient_invite_inviter_name";

interface InviteContext {
  inviteToken: string;
  invitingPatientName: string;
}

/**
 * Reads invite-link context for the friend-invite flow. Returns `null`
 * when the user is not currently on an invite-driven URL.
 *
 * The URL query param (`?invite_token=`) is the source of truth for
 * "is this an invite-driven page right now?". The cookie set by
 * `PublicInviteLandingController` is consulted only as a name lookup
 * for the matching token — it never activates the banner on its own.
 *
 * This intentional URL-required design avoids stale-cookie bleed:
 * once a user follows an invite link, the cookie lives for 24h, but
 * we don't want the banner to render on every other welcome URL the
 * user happens to visit during that window (different inviter, no
 * inviter, different store, etc.). The token must be on the URL the
 * user is actually viewing.
 *
 * If a future flow needs to recover the token after the URL is
 * stripped (e.g. an OAuth round-trip), that flow is responsible for
 * re-attaching `?invite_token=` to its redirect-back URL.
 */
const useInviteContext = (): InviteContext | null => {
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get("invite_token");

  return useMemo(() => {
    if (!inviteToken) return null;

    const cookies = readCookies();
    const cookieToken = cookies[INVITE_TOKEN_COOKIE];
    if (cookieToken && cookieToken !== inviteToken) return null;

    const invitingPatientName = cookies[INVITE_INVITER_NAME_COOKIE];
    if (!invitingPatientName) return null;

    return { inviteToken, invitingPatientName };
  }, [inviteToken]);
};

const readCookies = (): Record<string, string> => {
  if (typeof document === "undefined") return {};

  return document.cookie.split("; ").reduce<Record<string, string>>((acc, pair) => {
    const [name, ...rest] = pair.split("=");
    if (!name) return acc;
    acc[name] = decodeURIComponent(rest.join("="));

    return acc;
  }, {});
};

export { useInviteContext };
export type { InviteContext };
